import React, { useState, useEffect } from "react";
import {
  UserRound,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

export default function RegisterModal({ onClose, inline = false }) {
  const [accountType, setAccountType] = useState("user");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // OTP verification state
  const [step, setStep] = useState("register");
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(58);
  const otpInputRefs = React.useRef([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    agreeToTerms: false,
  });

useEffect(() => {
  if (inline) return;

  const handleEscape = (event) => {
    if (event.key === "Escape" && !loading) {
      onClose();
    }
  };

  document.addEventListener("keydown", handleEscape);

  return () => {
    document.removeEventListener("keydown", handleEscape);
  };
}, [onClose, loading, inline]);

  useEffect(() => {
    if (step !== "otp" || resendSeconds <= 0) return;

    const timer = setTimeout(() => {
      setResendSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [step, resendSeconds]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (loading) return;

    if (
      formData.password.length < 8 ||
      !/[A-Za-z]/.test(formData.password) ||
      !/\d/.test(formData.password)
    ) {
      setError(
        "Password must be at least 8 characters and include a letter and a number."
      );
      return;
    }

    if (!formData.agreeToTerms) {
      setError("Please agree to the Terms of Use and Privacy Policy.");
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      setError("API URL is not configured. Please contact support.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          username: formData.email.trim().toLowerCase(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          accountType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.msg || data.error || "Registration failed. Please try again."
        );
      }

      // Registration succeeded. Show OTP verification in the same card.
      setOtpEmail(formData.email.trim().toLowerCase());
      setOtp("");
      setOtpError("");
      setResendSeconds(58);
      setStep("otp");
    } catch (err) {
      setError(
        err.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setOtpError("");

    if (verifying) return;

    if (otp.length !== 4) {
      setOtpError("Please enter the 4-digit verification code.");
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      setOtpError("API URL is not configured. Please contact support.");
      return;
    }

    setVerifying(true);

    try {
      const response = await fetch(`${apiUrl}/auth/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: otpEmail,
          otp,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.msg || data.error || "Invalid or expired OTP."
        );
      }

      // Do not show an additional success screen.
      if (onClose) {
        onClose();
      } else {
        window.location.href = "/login";
      }
    } catch (err) {
      setOtpError(
        err.message || "Verification failed. Please try again."
      );
    } finally {
      setVerifying(false);
    }
  };

  const inputClass =
    "h-[35px] w-full rounded-md border border-[#dce1e9] bg-white pl-[37px] pr-3 text-[12px] text-[#18294c] outline-none placeholder:text-[#71809b] focus:border-[#0758b5] focus:ring-1 focus:ring-blue-100";

  const iconClass =
    "pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-[#7b879c]";

  return (
    <div
      className={
        inline
          ? "flex w-full items-center justify-center"
          : "fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-white/10 px-4 py-6 backdrop-blur-md"
      }
      onMouseDown={
        inline
          ? undefined
          : (event) => {
              if (event.target === event.currentTarget && !loading) {
                onClose();
              }
            }
      }
    >
      <div
        role="dialog"
        aria-modal={!inline}
        aria-labelledby="register-title"
      className={`relative w-full ${
  inline
    ? "max-h-full max-w-[460px] overflow-y-auto rounded-xl border border-[#e4e8ef] bg-white p-5 shadow-2xl sm:p-7"
    : "my-auto max-h-[calc(100dvh-32px)] max-w-[540px] overflow-y-auto rounded-xl border border-[#e4e8ef] bg-white p-6 shadow-2xl sm:p-7"
}`}
      >
        {/* Close Button */}
      {onClose && <button
          type="button"
          onClick={onClose}
          disabled={loading}
          aria-label="Close registration"
          className="absolute right-3 top-3 rounded-full p-1.5 text-[#9aa4b6] transition hover:bg-gray-100 hover:text-[#172748] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>}

        {step === "register" ? (
          <>
        {/* Heading */}
        <div className="mb-[15px]">
          <h2
            id="register-title"
            className="text-[25px] font-bold leading-[30px] text-[#102044]"
          >
            Create Your Account
          </h2>

          <p className="mt-[2px] text-[12px] leading-[17px] text-[#64718b]">
            Join BizNep to discover and support local businesses.
          </p>
        </div>

        {/* Account Type */}
        <div className="mb-[11px] grid grid-cols-2 gap-3">
          {/* User Account */}
          <button
            type="button"
            onClick={() => {
              setAccountType("user");
              setError("");
            }}
            disabled={loading}
            className={`relative flex h-[73px] items-center gap-[10px] rounded-lg border px-[10px] text-left transition ${
              accountType === "user"
                ? "border-[#1764ff] bg-[#eef5ff]"
                : "border-[#e0e4eb] bg-white hover:border-blue-300"
            }`}
          >
            <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#dceaff] text-[#0758d5]">
              <svg
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="currentColor"
              >
                <circle cx="12" cy="7.5" r="4" />
                <path d="M3.5 21c0-4.7 3.8-8 8.5-8s8.5 3.3 8.5 8H3.5Z" />
              </svg>
            </span>

            <span className="min-w-0">
              <span className="block whitespace-nowrap text-[12px] font-semibold text-[#172748]">
                User Account
              </span>

              <span className="mt-[2px] block text-[11px] leading-[16px] text-[#64718b]">
                Find and explore
                <br />
                local businesses
              </span>
            </span>

            <span
              className={`absolute right-[8px] top-[9px] flex h-[15px] w-[15px] items-center justify-center rounded-full border ${
                accountType === "user"
                  ? "border-[#0863f7] bg-[#0863f7]"
                  : "border-[#c3c9d4] bg-white"
              }`}
            >
              {accountType === "user" && (
                <span className="h-[7px] w-[7px] rounded-full bg-white" />
              )}
            </span>
          </button>

          {/* Business Account */}
          <button
            type="button"
            onClick={() => {
              setAccountType("business");
              setError("");
            }}
            disabled
            className={`relative flex h-[73px] items-center gap-[10px] rounded-lg border px-[10px] text-left transition ${
              accountType === "business"
                ? "border-[#1764ff] bg-[#eef5ff]"
                : "border-[#e0e4eb] bg-white hover:border-blue-300"
            }`}
          >
            <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#ffe2e4] text-[#e3262e]">
              <svg
                viewBox="0 0 24 24"
                className="h-6 w-6"
                fill="currentColor"
              >
                <path d="M3 9.5 5 4h14l2 5.5v2a2.5 2.5 0 0 1-2.5 2.5 2.5 2.5 0 0 1-2.5-2 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0A2.5 2.5 0 0 1 3 11.5v-2Z" />
                <path d="M5 13.5V21h14v-7.5a4 4 0 0 1-3-1.5 4 4 0 0 1-8 0 4 4 0 0 1-3 1.5Z" />
                <path d="M9 21v-5h6v5" fill="white" />
              </svg>
            </span>

            <span className="min-w-0">
              <span className="block whitespace-nowrap text-[12px] font-semibold text-[#172748]">
                Business Account
              </span>

              <span className="mt-[2px] block text-[11px] leading-[16px] text-[#64718b]">
                List and manage
                <br />
                your business
              </span>
            </span>

            <span
              className={`absolute right-[8px] top-[9px] flex h-[15px] w-[15px] items-center justify-center rounded-full border ${
                accountType === "business"
                  ? "border-[#0863f7] bg-[#0863f7]"
                  : "border-[#c3c9d4] bg-white"
              }`}
            >
              {accountType === "business" && (
                <span className="h-[7px] w-[7px] rounded-full bg-white" />
              )}
            </span>
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-[9px]">
          {/* First Name */}
          <div className="relative">
            <UserRound size={16} className={iconClass} />

            <input
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First Name *"
              autoComplete="given-name"
              required
              disabled={loading}
              className={inputClass}
            />
          </div>

          {/* Last Name */}
          <div className="relative">
            <UserRound size={16} className={iconClass} />

            <input
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last Name *"
              autoComplete="family-name"
              required
              disabled={loading}
              className={inputClass}
            />
          </div>

          {/* Email */}
          <div className="relative">
            <Mail size={16} className={iconClass} />

            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email (will be your username) *"
              autoComplete="email"
              required
              disabled={loading}
              className={inputClass}
            />
          </div>

          {/* Phone */}
          <div className="relative">
            <Phone size={16} className={iconClass} />

            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number (Optional)"
              autoComplete="tel"
              disabled={loading}
              className={inputClass}
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock size={16} className={iconClass} />

            <input
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              placeholder="Password *"
              autoComplete="new-password"
              minLength={8}
              pattern="(?=.*[A-Za-z])(?=.*[0-9]).{8,}"
              title="Password must be at least 8 characters and include a letter and a number."
              required
              disabled={loading}
              className={`${inputClass} pr-9`}
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[#7b879c] hover:text-[#172748]"
            >
              {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </div>

          {/* Password Hint */}
          <p className="px-[2px] text-[10px] leading-[14px] text-[#71809b]">
            Password must be at least 8 characters and include a letter and a
            number.
          </p>

          {/* Terms */}
          <label className="flex cursor-pointer items-start gap-[8px] text-[12px] leading-[18px] text-[#18294c]">
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              required
              disabled={loading}
              className="mt-[1px] h-[16px] w-[16px] shrink-0 cursor-pointer accent-[#0758b5]"
            />

            <span>
              I agree to the{" "}
              <a
                href="/terms"
                className="font-semibold text-[#0758b5] hover:underline"
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                className="font-semibold text-[#0758b5] hover:underline"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>

          {/* Error Message */}
          {error && (
            <p
              role="alert"
              className="rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-600"
            >
              {error}
            </p>
          )}

          {/* Success Message */}
          {success && (
            <div
              role="status"
              className="rounded-md bg-green-50 px-3 py-3 text-[12px] text-green-700"
            >
              {success}
              <p className="mt-1">
                Please keep your email address handy for OTP verification.
              </p>
            </div>
          )}

          {/* Create Account */}
          <button
            type="submit"
            disabled={loading}
            className="h-[35px] w-full rounded-md bg-[#e92329] text-[14px] font-bold text-white transition hover:bg-[#d51e25] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {/* OR */}
        <div className="my-[10px] flex items-center gap-3">
          <div className="h-px flex-1 bg-[#e0e4eb]" />

          <span className="text-[11px] font-semibold text-[#9aa4b6]">
            OR
          </span>

          <div className="h-px flex-1 bg-[#e0e4eb]" />
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={() => console.log("Google sign-in clicked")}
          className="flex h-[35px] w-full items-center justify-center gap-[10px] rounded-md border border-[#dce1e9] bg-white text-[12px] font-semibold text-[#172748] transition hover:bg-gray-50"
        >
          <svg
            viewBox="0 0 48 48"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              fill="#4285F4"
              d="M43.6 24.5c0-1.4-.1-2.8-.4-4.1H24v7.8h11c-.5 2.5-2 4.6-4.2 6l6.8 5.3c4-3.7 6-9.1 6-15Z"
            />
            <path
              fill="#34A853"
              d="M24 44c5.5 0 10.1-1.8 13.5-4.8l-6.8-5.3c-1.9 1.3-4.3 2.1-6.7 2.1-5.2 0-9.6-3.5-11.2-8.2l-7 5.4C9.7 39.6 16.3 44 24 44Z"
            />
            <path
              fill="#FBBC05"
              d="M12.8 27.8a12 12 0 0 1 0-7.6l-7-5.4a20 20 0 0 0 0 18.4l7-5.4Z"
            />
            <path
              fill="#EA4335"
              d="M24 9.5c3 0 5.7 1 7.8 3.1l5.8-5.8C34.1 3.5 29.5 1.5 24 1.5 16.3 1.5 9.7 5.9 5.8 12.2l7 5.4C14.4 13 18.8 9.5 24 9.5Z"
            />
          </svg>

          Continue with Google
        </button>

        {/* Sign In */}
        <p className="mt-[13px] text-center text-[12px] text-[#64718b]">
          Already have an account?{" "}
          <a
            href="/login"
            onClick={inline ? undefined : onClose}
            className="font-semibold text-[#e92329] hover:underline"
          >
            Sign In
          </a>
        </p>
          </>
        ) : (
          <div className="flex flex-col items-center px-0 py-2 text-center">
            <div className="mb-3 flex h-[76px] w-[76px] items-center justify-center rounded-full bg-[#e8f1ff]">
              <Mail size={40} className="text-[#1764ff]" />
            </div>

            <h2 className="mb-1 text-[25px] font-bold leading-[30px] text-[#102044]">
              Check Your Email
            </h2>

            <p className="mb-8 text-[15px] leading-6 text-[#64718b]">
              We have sent a 4-digit verification code to
              <br />
              <strong className="text-[#46536f]">{otpEmail}</strong>
            </p>

            <form onSubmit={handleVerifyOtp} className="w-full">
              <div className="mb-6 flex justify-center gap-2">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    ref={(element) => (otpInputRefs.current[index] = element)}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    value={otp[index] || ""}
                    onChange={(event) => {
                      const digit = event.target.value.replace(/\D/g, "").slice(-1);
                      const nextOtp = otp.split("");
                      nextOtp[index] = digit;
                      const updatedOtp = nextOtp.join("").slice(0, 4);
                      setOtp(updatedOtp);
                      setOtpError("");
                      if (digit && index < 3) {
                        otpInputRefs.current[index + 1]?.focus();
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Backspace" && !otp[index] && index > 0) {
                        otpInputRefs.current[index - 1]?.focus();
                      }
                    }}
                    aria-label={`Verification code digit ${index + 1}`}
                    className="h-[60px] w-[62px] rounded-lg border border-[#d4dbe8] bg-white text-center text-[24px] text-[#18294c] outline-none focus:border-[#1764ff] focus:ring-2 focus:ring-blue-100"
                  />
                ))}
              </div>

              <p className="mb-6 text-[13px] text-[#64718b]">
                Didn't receive the code?{" "}
                <span className="font-semibold text-[#0758d5]">
                  Resend Code
                </span>
                {resendSeconds > 0 && (
                  <span className="font-semibold text-[#0758d5]">
                    {" "}({`00:${String(resendSeconds).padStart(2, "0")}`})
                  </span>
                )}
              </p>

              {otpError && (
                <p role="alert" className="mb-4 text-sm text-red-600">
                  {otpError}
                </p>
              )}

              <button
                type="submit"
                disabled={verifying || otp.length !== 4}
                className="h-[45px] w-full rounded-md bg-[#e92329] text-[16px] font-bold text-white transition hover:bg-[#d51e25] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {verifying ? "Verifying..." : "Verify Email"}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setStep("register");
                setOtp("");
                setOtpError("");
                setError("");
              }}
              className="mt-8 text-[13px] font-semibold text-[#0758d5] transition hover:underline"
            >
              <span aria-hidden="true">←</span>
              {" "}Back to Sign Up
            </button>
          </div>
        )}
      </div>
    </div>
  );
}